import { useState } from 'react';
import type { RiskAssessment, Task } from '../lib/types';
import { useRefinePlan } from '../api/tasks';
import { formatTokens, totalTokens } from '../lib/format';

interface Props {
  task: Task;
  projectSlug: string;
  onApprove: (approved: boolean, reason?: string) => void;
}

/**
 * Tailwind classes per risk level. high gets the strongest treatment
 * because the gate only ever pauses when something genuinely warrants
 * a human look — most plans are low/medium and pass through silently.
 */
const RISK_BADGE_STYLE: Record<NonNullable<Task['risk_overall']>, string> = {
  low: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-400',
  high: 'bg-red-100 text-red-800 border-red-400',
};

const RISK_BADGE_LABEL: Record<NonNullable<Task['risk_overall']>, string> = {
  low: '· Low risk',
  medium: '● Medium risk',
  high: '⚠ High risk',
};

// Defensive against a partial/legacy risk_assessment: a missing array or
// number must never crash the whole approval page (it did — an undefined
// touches_protected_paths white-screened TaskDetail).
function formatRiskSignals(r: RiskAssessment): string[] {
  const sigs: string[] = [];
  if ((r.high_severity_concerns ?? 0) > 0) {
    sigs.push(`${r.high_severity_concerns} high-severity critic concern(s)`);
  }
  if ((r.touches_protected_paths?.length ?? 0) > 0) {
    sigs.push(`touches protected paths: ${r.touches_protected_paths.join(', ')}`);
  }
  if (r.modifies_dependencies) sigs.push('modifies dependency manifests / lockfiles');
  if (r.modifies_ci_config) sigs.push('modifies CI config');
  if (r.modifies_migrations) sigs.push('modifies DB migrations');
  if (r.step_count_exceeds_normal) sigs.push('larger than typical plan size');
  if ((r.unclear_acceptance ?? 0) >= 2) {
    sigs.push(`${r.unclear_acceptance} step(s) with unverifiable acceptance criteria`);
  }
  return sigs;
}

export default function ApprovalPanel({ task, projectSlug, onApprove }: Props) {
  const [reason, setReason] = useState('');
  const [refining, setRefining] = useState(false);
  const [feedback, setFeedback] = useState('');
  const refine = useRefinePlan(task.id, projectSlug);

  const tokensSoFar = totalTokens(task);
  const riskSignals =
    task.risk_assessment ? formatRiskSignals(task.risk_assessment) : [];

  const submitFeedback = (): void => {
    const trimmed = feedback.trim();
    if (!trimmed) return;
    refine.mutate(trimmed, {
      onSuccess: () => {
        setRefining(false);
        setFeedback('');
      },
    });
  };

  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 mb-4 sticky top-0 z-10 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-amber-900">
              ⏸ Plan is waiting for your approval
            </h3>
            {task.risk_overall && (
              <span
                className={
                  'inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ' +
                  RISK_BADGE_STYLE[task.risk_overall]
                }
                title={
                  riskSignals.length > 0
                    ? riskSignals.join('\n')
                    : 'No risk signals detected'
                }
              >
                {RISK_BADGE_LABEL[task.risk_overall]}
              </span>
            )}
          </div>
          <p className="text-sm text-amber-800">
            Review the plan below. Approve to execute, request changes to send
            feedback to the architect, or reject to cancel.
          </p>
          {task.risk_overall === 'high' && riskSignals.length > 0 && (
            <ul className="mt-2 text-xs text-red-800 list-disc list-inside space-y-0.5">
              {riskSignals.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Token usage so far */}
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wide text-amber-700">
            Tokens used (planning)
          </div>
          <div className="text-xl font-bold text-amber-900 leading-tight">
            {formatTokens(tokensSoFar)}
          </div>
          <div className="text-[10px] text-amber-700">
            {formatTokens(Number(task.total_input_tokens) || 0)} in / {formatTokens(Number(task.total_output_tokens) || 0)} out
          </div>
        </div>
      </div>

      {!refining && (
        <>
          <div className="mt-3">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional note (visible in the comments thread)"
              className="w-full rounded border border-amber-300 px-2 py-1 text-sm bg-white"
            />
          </div>

          <div className="mt-3 flex gap-2 flex-wrap">
            <button
              onClick={() => onApprove(true, reason || undefined)}
              className="flex-1 min-w-[120px] px-3 py-2 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700"
            >
              ✓ Approve &amp; run
            </button>
            <button
              onClick={() => setRefining(true)}
              className="px-3 py-2 text-sm rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
              title="Send feedback to the architect for a revised plan"
            >
              💬 Request changes
            </button>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    'Reject this plan? The task will be marked as cancelled.',
                  )
                ) {
                  onApprove(false, reason || undefined);
                }
              }}
              className="px-3 py-2 text-sm rounded border border-red-300 text-red-700 hover:bg-red-50"
            >
              ✗ Reject
            </button>
          </div>
        </>
      )}

      {refining && (
        <div className="mt-3 bg-white border border-amber-300 rounded p-3">
          <label className="block text-xs font-medium text-slate-700 mb-1">
            What should the architect change?
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={4}
            autoFocus
            placeholder={
              'Example: "Split step 2 into two smaller steps — first validate, then apply. ' +
              'Also use the existing AuthService instead of creating a new one."'
            }
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <p className="text-xs text-slate-500 mt-1">
            The architect will produce a revised plan addressing your feedback.
          </p>
          {refine.error && (
            <div className="text-xs text-red-700 mt-1">
              {(refine.error as Error).message}
            </div>
          )}
          <div className="mt-3 flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setRefining(false);
                setFeedback('');
              }}
              disabled={refine.isPending}
              className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitFeedback}
              disabled={!feedback.trim() || refine.isPending}
              className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {refine.isPending ? 'Submitting…' : 'Send to architect'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
